import {
  ColDef,
  GridOptions,
  themeAlpine,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
  ExperienceListVariationDto,
  GenericExperienceFe,
  VariationFe,
} from "@intelligems/ig-types";
import { Grid, styled } from "@mui/material";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { rolloutGraphConfig } from "@src/features/analytics/analyticsChartConfigs";
import {
  currencyFormatter,
  noDecimalNumberFormatter,
} from "@src/features/analytics/dataFormatters";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import {
  GroupCellRenderer,
  TableGenericCellRenderer,
} from "@src/features/analytics/tables/analyticsSegmentTable/audienceSegmentTableRenderers";
import {
  normalizeValues,
  sortAndFlattenData,
} from "@src/features/analytics/tables/analyticsSegmentTable/utils";
import { GraphConfig, GraphType } from "@src/features/analytics/types";
import { shouldShowProfitPlaceholder } from "@src/features/analytics/utils";
import { theme } from "@src/theme";
import { sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  pinnedColumnBorder: "1px solid #F5F5F5",
});
const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:last-child:after`]: {
    backgroundColor: "transparent",
  },
  "& ::-webkit-scrollbar": {
    width: 0 /* Remove scrollbar space */,
    background: "transparent" /* Optional: just make scrollbar invisible */,
  },
  [`& .ag-root-wrapper`]: {
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
    border: "none",
  },
  [`& .ag-header`]: {
    borderBottom: "1px solid #F5F5F5",
  },
  [`& .ag-header-cell`]: {
    paddingLeft: "14px !important",
    paddingRight: "0px !important",
  },
  [`& .ag-row:last-child`]: {
    borderBottom: "solid 0px !important",
  },
  [`& .ag-row`]: {
    borderBottom: "1px solid #F5F5F5",
  },
  [`& .ag-header-cell-label`]: {
    fontWeight: 400,
    fontSize: "14px",
    borderRight: "none",
    fontFamily: `${theme.typography.fontFamily}`,
    padding: "0 !important",
  },
  [`& .ag-header-cell-comp-wrapper`]: {
    padding: "0 15px 0 0 !important ",
    justifyContent: "flex-end",
  },
  [`& .ag-cell-wrap-text`]: {
    wordBreak: "break-word",
  },
  [`&.ag-header-cell`]: {
    backgroundColor: "blue !important",
    borderRight: "none !important",
  },
  [`& .ag-header-cell:after`]: {
    display: "none",
  },
  [`& .ag-header-cell-text`]: {
    color: `#6C6C6C !important`,
    fontWeight: 600,
    fontSize: "14px",
  },
  [`& .ag-pinned-left-header`]: {
    borderRight: "none !important",
    paddingLeft: "15px",
  },
  [`& .ag-cell-norm`]: {
    [`&.ag-cell`]: {
      fontSize: "14px !important",
      color: theme.palette.text.secondary,
    },
  },
  [`& .ag-cell-end`]: {
    [`&.ag-cell`]: {
      fontSize: "14px !important",
      color: theme.palette.text.secondary,
    },
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-popup-child:not(.ag-tooltip-custom)`]: {
    backgroundColor: "white",
    borderRadius: "10px",
    fontFamily: `${theme.typography.fontFamily}`,
    fontSize: "14px",
    fontWeight: 400,
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "90px !important",
  },
  // [`& .ag-pinned-left-header`]: {
  //   borderRight: "none",
  // },
}));

interface RolloutAnalyticsTableProps {
  experience: ExperienceFe | GenericExperienceFe;
  variations:
    | VariationFe[]
    | (Omit<ExperienceListVariationDto, "experienceId"> & {
        experienceId?: string;
      })[];
  onRowSelected?: (id: string) => void;
  selectedVariationId: string | null;
  setSelectedVariationId: (id: string) => void;
}

export const RolloutAnalyticsTable = forwardRef(
  (
    {
      variations,
      experience,
      onRowSelected,
      selectedVariationId,
      setSelectedVariationId,
    }: RolloutAnalyticsTableProps,
    ref
  ) => {
    const dashboardData = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: rolloutGraphConfig,
        experience: experience,
        overviewDataLoaded: true,
        keyCustomEvents: [],
      },
      "experiences"
    );

    const controlVariationId =
      variations.find((variation) => variation.isControl)?.id ||
      variations[0].id ||
      "";
    const graphValueFormatter = useGraphValueFormatter();
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const shouldShowProfit = useConfiguration<boolean>(
      "should_show_profit_in_dashboard"
    );
    const gridRef = useRef<AgGridReact>(null);
    const isOverviewFetching = useAnalyticsFetching();
    const [internalSelectedRowId, setInternalSelectedRowId] = useState<
      string | null
    >(null);
    const selectedRowId = selectedVariationId ?? internalSelectedRowId;
    const setSelectedRowId = setSelectedVariationId ?? setInternalSelectedRowId;

    const handleRowSelection = (id: string) => {
      setSelectedRowId(id);
      if (onRowSelected && id !== selectedRowId) {
        onRowSelected(id);
      }
    };

    const selectedData = useMemo(() => {
      return dashboardData;
    }, [dashboardData]);

    const rows = useMemo(() => {
      if (
        variations.length > 0 &&
        (!selectedData[0].data?.metrics ||
          !selectedData[0].data?.metrics?.length)
      ) {
        return variations.map((variation) => {
          return {
            variation_id: variation.id,
            n_visitors: { value: undefined },
            n_orders: { value: undefined },
            net_revenue: { value: undefined },
            net_revenue_per_order: { value: undefined },
            net_revenue_per_visitor: { value: undefined },
            pct_revenue_with_cogs: undefined,
            conversion_rate: undefined,
            gross_profit: undefined,
            gross_profit_per_visitor: undefined,
            order: variation.order,
          };
        });
      }

      const normalizedData = selectedData[0]?.data?.metrics.map(
        (group: any) => ({
          ...normalizeValues(group),
          order:
            group.variation_id === controlVariationId
              ? 0
              : (variations.find(
                  (variation) => variation.id === group.variation_id
                )?.order || 0) + 1,
        })
      );

      // Group by category
      const groups: Record<string, any[]> = {};
      normalizedData.forEach((item) => {
        const key = item[0];
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      // Sort variations
      const sortedGroups = { ...groups };
      Object.keys(sortedGroups).forEach((key) => {
        if (Array.isArray(sortedGroups[key])) {
          sortedGroups[key] = sortedGroups[key].sort(sortByOrder);
        }
      });

      // Sort grouped rows by analytics sorting order
      const sortedData = sortAndFlattenData(
        sortedGroups,
        selectedData[0]?.data?.audienceOrder || []
      );

      return sortedData.map((metric, index) => ({
        ...metric,
        id: metric.variation_id,
      }));
    }, [selectedData, variations, controlVariationId]);

    const defaultColDef: ColDef = {
      flex: 1,
      minWidth: 100,
      maxWidth: 350,
      resizable: true,
      cellStyle: {
        fontSize: "14px",
        alignContent: "center",
        alignItems: "right",
      },
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    };

    const filteredGraphConfigs = rolloutGraphConfig.filter((config) => {
      return !shouldShowProfitPlaceholder(shouldShowProfit?.value, config);
    });

    const columnDefs: ColDef[] = useMemo(() => {
      return [
        {
          headerName: "Group",
          headerClass: "header-group",
          field: "variation_id",
          flex: 0.7,
          minWidth: 100,
          pinned: true,
          cellStyle: {
            className: "ag-cell-group",
            justifyContent: "flex-start",
            borderRight: "1px solid #F5F5F5",
          },
          cellRendererParams: {
            variations,
            // Pass radio button props to GroupCellRenderer
            selectedRowId: selectedRowId,
            onRowSelected: handleRowSelection,
            disabled: false,
            disabledReason: "",
            tabValue: 0,
            isRolloutRender: true,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return (
              variations.find((variation) => variation.id === params.value)
                ?.name || "-"
            );
          },
          cellRenderer: GroupCellRenderer,
        },
        {
          headerName: "Visitors",
          field: "n_visitors",
          flex: 0.6,
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return noDecimalNumberFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "-";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        {
          headerName: "Orders",
          flex: 0.4,
          field: "n_orders",
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return noDecimalNumberFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "-";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        {
          headerName: "Net Revenue",
          flex: 0.5,
          field: "net_revenue",
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value?.value !== undefined) {
              return currencyFormatter(
                params.value?.value,
                false,
                storeCurrencyConfig!.value
              );
            } else return "-";
          },
          cellRenderer: TableGenericCellRenderer,
        },
        ...filteredGraphConfigs.map((config, index) => {
          const headerLabel = config.title[0];

          return {
            headerName: headerLabel,
            flex: experience.category === "experiment" ? 0.7 : 0.4,
            minWidth: 150,
            field: config.metrics[0],
            cellDataType: false,
            valueFormatter: (params: ValueFormatterParams) => {
              const shouldShowThreeDecimals =
                Math.min(
                  ...(dashboardData[0].data?.metrics || []).map(
                    (d) => d[config.impactMetric ?? config.metrics[0]]?.value
                  )
                ) < 0.09;
              if (params.value?.value !== undefined) {
                return (
                  graphValueFormatter({
                    isAxisTick: false,
                    value: params.value?.value,
                    configDataTypes: config.dataTypes,
                    decimalAmountShown: shouldShowThreeDecimals ? 3 : undefined,
                  }) || ""
                );
              } else return "-";
            },
            cellStyle: { justifyContent: "flex-start" },
            // cellRenderer: MetricCellRenderer,
          };
        }),
      ];
    }, [
      experience,
      // eslint-disable-next-line react-compiler/react-compiler
      selectedData[0].data,

      selectedData[0].isLoading,
      shouldShowProfit,
      selectedRowId,
      variations,
      controlVariationId,
      dashboardData,
      storeCurrencyConfig,
      graphValueFormatter,
      handleRowSelection,
    ]);

    useImperativeHandle(ref, () => ({
      api: gridRef.current?.api,
      getSelectedRowId: () => selectedRowId,
    }));

    const gridOptions: GridOptions = {
      context: {
        reportingCurrency: storeCurrencyConfig?.value?.code || "USD",
      },
    };

    return (
      <StyledGrid item container direction={"column"} sx={{ marginTop: "0px" }}>
        <AgGridReact
          ref={gridRef}
          theme={myTheme}
          defaultColDef={defaultColDef}
          rowData={isOverviewFetching > 0 ? [] : dashboardData ? rows : []}
          columnDefs={columnDefs}
          rowHeight={48}
          groupDefaultExpanded={1}
          loading={isOverviewFetching > 0}
          groupDisplayType={"groupRows"}
          suppressDragLeaveHidesColumns={true}
          suppressContextMenu={true}
          alwaysShowHorizontalScroll={true}
          alwaysShowVerticalScroll={true}
          suppressCellFocus={true}
          domLayout={"autoHeight"}
          ensureDomOrder={true}
          overlayLoadingTemplate={"<div>Loading Data... </div>"}
          overlayNoRowsTemplate={
            "<div>No Data - Your filters may have resulted in no data, try resetting them</div>"
          }
          suppressRowHoverHighlight={true}
          enableBrowserTooltips={true}
          gridOptions={gridOptions}
          onRowClicked={(params) => {
            if (params.data.variation_id) {
              handleRowSelection(params.data.variation_id);
            }
          }}
        />
      </StyledGrid>
    );
  }
);
